import { Delete, Search } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import ResponseSetTags from './ResponseSetTags'
import type {
  ResponseSetCategoryForm,
  ResponseSetForm,
} from './ResponseSetTypes'

type FilterKey = 'discriminationKey' | 'categories' | 'name'

type FilterValues = Record<FilterKey, string>

const PLACEHOLDER: Record<FilterKey, string> = {
  discriminationKey: '判別キー名を入力',
  categories: 'カテゴリー名を入力',
  name: '選択肢名を入力',
}

const emptyFilters: FilterValues = {
  discriminationKey: '',
  categories: '',
  name: '',
}

type EditableCellProps = {
  value: string
  onCommit: (value: string) => void
}

const EditableCell: FC<EditableCellProps> = ({
  value: initialValue,
  onCommit,
}) => {
  const [focused, setFocused] = useState(false)
  const [value, setValue] = useState(initialValue)

  return (
    <Tooltip title={!focused ? value : ''} placement="top">
      <TextField
        value={value}
        onChange={e => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false)
          onCommit(value)
        }}
        multiline
        {...(!focused && { rows: 1 })}
        fullWidth
        size="small"
      />
    </Tooltip>
  )
}

type OwnProps = {
  responses?: ResponseSetForm[]
  isLoadingResponses: boolean
  onDelete: (key: number) => void
  onChangeDiscriminationKey: (discriminationKey: string, key: number) => void
  onChangeName: (name: string, key: number) => void
  onInputConfirmTag: (responseKey: number, tagName: string) => void
  onCloseTag: (responseKey: number, tagIndex: number) => void
}

const ResponseSetTable: FC<OwnProps> = props => {
  const filterInputRef = useRef<HTMLInputElement>(null)
  const [filterInputValues, setFilterInputValues] =
    useState<FilterValues>(emptyFilters)
  const [appliedFilters, setAppliedFilters] =
    useState<FilterValues>(emptyFilters)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [openFilterKey, setOpenFilterKey] = useState<FilterKey | null>(null)

  const handleOpenFilter = (
    e: React.MouseEvent<HTMLElement>,
    key: FilterKey,
  ) => {
    setAnchorEl(e.currentTarget)
    setOpenFilterKey(key)
    setTimeout(() => filterInputRef.current?.select(), 100)
  }

  const handleSearch = () => {
    if (!openFilterKey) return
    setAppliedFilters(prev => ({
      ...prev,
      [openFilterKey]: filterInputValues[openFilterKey],
    }))
    setAnchorEl(null)
    setOpenFilterKey(null)
  }

  const handleClear = () => {
    if (!openFilterKey) return
    setFilterInputValues(prev => ({ ...prev, [openFilterKey]: '' }))
    setAppliedFilters(prev => ({ ...prev, [openFilterKey]: '' }))
    setAnchorEl(null)
    setOpenFilterKey(null)
  }

  const handleCloseFilter = () => {
    setAnchorEl(null)
    setOpenFilterKey(null)
  }

  const filteredResponses = (props.responses ?? []).filter(response => {
    const dkFilter = appliedFilters.discriminationKey.toLowerCase()
    const catFilter = appliedFilters.categories.toLowerCase()
    const nameFilter = appliedFilters.name.toLowerCase()

    if (
      dkFilter &&
      !(response.discriminationKey ?? '').toLowerCase().includes(dkFilter)
    ) {
      return false
    }
    if (
      catFilter &&
      !(response.categories ?? [])
        .map((c: ResponseSetCategoryForm) => c.name ?? '')
        .join(',')
        .toLowerCase()
        .includes(catFilter)
    ) {
      return false
    }
    if (
      nameFilter &&
      !(response.name ?? '').toLowerCase().includes(nameFilter)
    ) {
      return false
    }
    return true
  })

  const renderFilterableHeader = (title: string, key: FilterKey) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      {title}
      <IconButton
        size="small"
        aria-label={`${title}を検索`}
        onClick={e => handleOpenFilter(e, key)}
        color={appliedFilters[key] !== '' ? 'primary' : 'default'}
      >
        <Search fontSize="small" />
      </IconButton>
    </Stack>
  )

  return (
    <>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell size="small" sx={{ width: '20%' }}>
                {renderFilterableHeader('判別キー', 'discriminationKey')}
              </TableCell>
              <TableCell size="small" sx={{ width: '30%' }}>
                {renderFilterableHeader('カテゴリー', 'categories')}
              </TableCell>
              <TableCell size="small" sx={{ width: '45%' }}>
                {renderFilterableHeader('選択肢名', 'name')}
              </TableCell>
              <TableCell size="small" aria-label="操作" sx={{ width: '5%' }} />
            </TableRow>
          </TableHead>
          <TableBody sx={{ bgcolor: 'background.paper' }}>
            {props.isLoadingResponses ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : (
              filteredResponses.map(responseSet => (
                <TableRow key={responseSet.key}>
                  <TableCell size="small">
                    <EditableCell
                      value={responseSet.discriminationKey ?? ''}
                      onCommit={text =>
                        props.onChangeDiscriminationKey(text, responseSet.key)
                      }
                    />
                  </TableCell>
                  <TableCell size="small">
                    <ResponseSetTags
                      tags={
                        (responseSet.categories ??
                          []) as ResponseSetCategoryForm[]
                      }
                      onInputConfirm={(tagName: string) =>
                        props.onInputConfirmTag(responseSet.key, tagName)
                      }
                      onClose={(index: number) =>
                        props.onCloseTag(responseSet.key, index)
                      }
                    />
                  </TableCell>
                  <TableCell size="small">
                    <EditableCell
                      value={responseSet.name ?? ''}
                      onCommit={text =>
                        props.onChangeName(text, responseSet.key)
                      }
                    />
                  </TableCell>
                  <TableCell size="small">
                    <IconButton
                      aria-label="選択肢を削除"
                      color="error"
                      size="small"
                      onClick={() => props.onDelete(responseSet.key)}
                    >
                      <Delete color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Popover
        open={openFilterKey !== null}
        anchorEl={anchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack spacing={1} sx={{ p: 1 }}>
          <TextField
            inputRef={filterInputRef}
            size="small"
            placeholder={openFilterKey ? PLACEHOLDER[openFilterKey] : ''}
            value={openFilterKey ? filterInputValues[openFilterKey] : ''}
            slotProps={{
              htmlInput: {
                'aria-label': openFilterKey
                  ? PLACEHOLDER[openFilterKey]
                  : '検索条件を入力',
              },
            }}
            onChange={e => {
              if (!openFilterKey) return
              setFilterInputValues(prev => ({
                ...prev,
                [openFilterKey]: e.target.value,
              }))
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSearch()
            }}
            sx={{ width: 188 }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              sx={{ width: 90 }}
            >
              検索
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClear}
              sx={{ width: 90 }}
            >
              クリア
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}

export default ResponseSetTable
